import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import EventIcon from "@material-ui/icons/Event";
import PhoneIcon from "@material-ui/icons/Phone";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Card from "common/components/Card/Card.jsx";
import CardHeader from "common/components/Card/CardHeader.jsx";
import CardIcon from "common/components/Card/CardIcon.jsx";
import CardBody from "common/components/Card/CardBody.jsx";
import GridContainer from "common/components/Grid/GridContainer.jsx";
import GridItem from "common/components/Grid/GridItem.jsx";
import Button from "common/components/CustomButtons/Button";
import LoadingBox from "common/components/LoadingBox";
import * as api from "core-module/services/api";
import config from "config.js";
import dashboardStyle from "assets/jss/material-dashboard-pro-react/views/dashboardStyle";
import withStyles from "@material-ui/core/styles/withStyles";

const styles = {
  cardIconTitle: dashboardStyle.cardIconTitle,
  cardTitle: { ...dashboardStyle.cardTitle, margin: "20px 0" },
  sectionIntro: {
    color: "#5f6f7e",
    margin: "4px 0 20px",
    lineHeight: 1.5,
  },
  optionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "18px",
    marginTop: "18px",
  },
  optionCard: {
    border: "1px solid #cfe4ef",
    borderRadius: "8px",
    background: "#fff",
    overflow: "hidden",
    boxShadow: "0 12px 28px rgba(20, 62, 103, 0.08)",
  },
  optionHeader: {
    padding: "16px 18px",
    background: "#1f77a6",
    color: "#fff",
  },
  optionDay: {
    margin: 0,
    color: "#fff",
    fontSize: "18px",
    fontWeight: 700,
  },
  optionDate: {
    margin: "6px 0 0",
    color: "#eaf6fb",
    fontSize: "13px",
  },
  optionBody: {
    padding: "18px",
  },
  optionTime: {
    margin: "0 0 14px",
    color: "#123e68",
    fontSize: "22px",
    fontWeight: 700,
  },
  optionCopy: {
    margin: "0 0 18px",
    color: "#5f6f7e",
    lineHeight: 1.45,
  },
  scheduledCard: {
    border: "1px solid #cfe4ef",
    borderRadius: "8px",
    background: "#fff",
    overflow: "hidden",
    boxShadow: "0 12px 28px rgba(20, 62, 103, 0.08)",
    marginTop: "18px",
  },
  scheduledMeta: {
    margin: "8px 0 0",
    color: "#5f6f7e",
    lineHeight: 1.5,
  },
  inlineError: {
    marginBottom: "18px",
    padding: "12px 14px",
    borderRadius: "6px",
    border: "1px solid #f3c2c2",
    background: "#fff5f5",
    color: "#b33a3a",
    fontWeight: 600,
  },
  actions: {
    marginTop: "20px",
  },
  earlierAppointment: {
    marginTop: "24px",
    padding: "14px 18px",
    border: "1px solid #dce5ec",
    borderRadius: "8px",
    background: "#f8fafc",
  },
  preferenceLabel: {
    margin: 0,
    alignItems: "flex-start",
    "& .MuiCheckbox-root": { padding: "4px 10px 4px 0" },
    "& .MuiFormControlLabel-label": {
      paddingTop: "5px",
      color: "#123e68",
      fontWeight: 600,
      lineHeight: 1.5,
    },
  },
  preferenceHelp: {
    margin: "4px 0 0 34px",
    color: "#5f6f7e",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  preferenceStatus: {
    margin: "4px 0 0 34px",
    color: "#315c73",
    fontSize: "13px",
    minHeight: "20px",
  },
  support: {
    marginTop: "24px",
    padding: "20px",
    border: "1px solid #cfe4ef",
    borderRadius: "8px",
    display: "flex",
    gap: "14px",
    alignItems: "flex-start",
    color: "#123e68",
    "& h5": { margin: "0 0 8px", fontWeight: 700 },
    "& p": { margin: "4px 0", lineHeight: 1.6 },
    "& a": { color: "#176b99", textDecoration: "underline" },
  },
};

const PreinstallSchedule = (props) => {
  const { classes } = props;
  const userid = useSelector((state) => state.services.ids.user);
  const [loading, setLoading] = useState(true);
  const [savingOptionId, setSavingOptionId] = useState(null);
  const [error, setError] = useState("");
  const [scheduleData, setScheduleData] = useState(null);
  const [savingPreference, setSavingPreference] = useState(false);
  const [preferenceSaved, setPreferenceSaved] = useState(false);

  useEffect(() => {
    let isMounted = true;
    api
      .get("user." + userid + "/preinstallschedule")
      .then((response) => {
        if (!isMounted) {
          return;
        }
        api.requireApiSuccess(
          response,
          "Unable to load install scheduling options."
        );
        setScheduleData(response?.object || response?.list?.[0] || response);
        setLoading(false);
      })
      .catch((e) => {
        if (!isMounted) {
          return;
        }
        setError(e.message || "Unable to load install scheduling options.");
        setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [userid]);

  const scheduleOption = async (optionid) => {
    if (savingPreference || savingOptionId !== null) return;
    setSavingOptionId(optionid);
    setError("");
    try {
      api.requireApiSuccess(
        await api.post("user." + userid + "/preinstallschedule", { optionid }),
        "Unable to schedule this install option."
      );
      props.history.push(
        config.MYACCOUNT.APP_PATH + "/user/preinstallchecklist"
      );
    } catch (e) {
      setError(e.message || "Unable to schedule this install option.");
      setSavingOptionId(null);
    }
  };

  const saveEarlierAppointment = async (wantsooner) => {
    if (savingPreference || savingOptionId !== null) return;
    const previousValue = scheduleData.wantsooner === true;
    setScheduleData((current) => ({ ...current, wantsooner }));
    setSavingPreference(true);
    setPreferenceSaved(false);
    setError("");
    try {
      const response = await api.post(
        "user." + userid + "/preinstallschedule",
        { wantsooner }
      );
      api.requireApiSuccess(
        response,
        "Unable to save your earlier-appointment preference. Please try again."
      );
      setScheduleData((current) => ({
        ...current,
        wantsooner: response.wantsooner === true,
      }));
      setPreferenceSaved(true);
    } catch (e) {
      setScheduleData((current) => ({ ...current, wantsooner: previousValue }));
      setError(
        e.message ||
          "Unable to save your earlier-appointment preference. Please try again."
      );
    } finally {
      setSavingPreference(false);
    }
  };

  const parseScheduleDate = (dateValue) => {
    const rawDate = String(dateValue || "");
    if (/^\d{8}$/.test(rawDate)) {
      return new Date(
        Number(rawDate.substring(0, 4)),
        Number(rawDate.substring(4, 6)) - 1,
        Number(rawDate.substring(6, 8))
      );
    }

    return new Date(rawDate);
  };

  const getAppointmentPeriod = (timeValue) => {
    const rawTime = String(timeValue || "");

    if (/^\d{12}$/.test(rawTime)) {
      return Number(rawTime.substring(8, 10)) < 12 ? "Morning" : "Afternoon";
    }

    if (/^\d{2}:\d{2}/.test(rawTime)) {
      return Number(rawTime.substring(0, 2)) < 12 ? "Morning" : "Afternoon";
    }

    const parsedTime = new Date(rawTime);
    if (!Number.isNaN(parsedTime.getTime())) {
      return parsedTime.getHours() < 12 ? "Morning" : "Afternoon";
    }

    return "";
  };

  const formatOptionDay = (option) => {
    const dateValue =
      option.date || String(option.startts || "").substring(0, 8);
    const parsedDate = parseScheduleDate(dateValue);
    if (!Number.isNaN(parsedDate.getTime())) {
      return {
        day: parsedDate.toLocaleDateString([], { weekday: "long" }),
        date: parsedDate.toLocaleDateString([], {
          month: "long",
          day: "numeric",
        }),
      };
    }

    return { day: dateValue, date: "" };
  };

  const formatScheduledDay = () => {
    const dateValue =
      scheduleData?.scheduleddate || String(scheduleData?.scheduledts || "");
    const parsedDate = parseScheduleDate(dateValue);
    if (!Number.isNaN(parsedDate.getTime())) {
      return {
        day: parsedDate.toLocaleDateString([], { weekday: "long" }),
        date: parsedDate.toLocaleDateString([], {
          month: "long",
          day: "numeric",
        }),
      };
    }

    return { day: dateValue, date: "" };
  };

  if (loading) {
    return <LoadingBox />;
  }

  const options = (scheduleData?.options || []).slice(0, 3);
  const scheduledDay = formatScheduledDay();
  const scheduledPeriod = getAppointmentPeriod(
    scheduleData?.scheduledtime || scheduleData?.scheduledts
  );

  return (
    <GridContainer justifyContent="center">
      <GridItem xs={12} md={10}>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <EventIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Schedule Install</h4>
          </CardHeader>
          <CardBody>
            {error ? (
              <div className={classes.inlineError} role="alert">
                {error}
              </div>
            ) : null}
            {scheduleData?.scheduled ? (
              <div>
                <h4 className={classes.cardTitle}>Your Install Is Scheduled</h4>
                <p className={classes.sectionIntro}>
                  We have your appointment date saved. The exact appointment
                  time has not been finalized yet and may change.
                </p>
                <div className={classes.scheduledCard}>
                  <div className={classes.optionHeader}>
                    <h4 className={classes.optionDay}>{scheduledDay.day}</h4>
                    <p className={classes.optionDate}>{scheduledDay.date}</p>
                  </div>
                  <div className={classes.optionBody}>
                    <h3 className={classes.optionTime}>
                      Exact time to be confirmed
                    </h3>
                    {scheduledPeriod ? (
                      <p className={classes.optionCopy}>
                        Estimated period: {scheduledPeriod}. Subject to change.
                      </p>
                    ) : null}
                    {scheduleData?.agentname ? (
                      <p className={classes.scheduledMeta}>
                        Installer: {scheduleData.agentname}
                      </p>
                    ) : null}
                    <p className={classes.scheduledMeta}>
                      MCSnet install appointment
                    </p>
                  </div>
                </div>
              </div>
            ) : options.length > 0 ? (
              <div>
                <h4 className={classes.cardTitle}>
                  Choose an Install Appointment
                </h4>
                <p className={classes.sectionIntro}>
                  These are the best available dates for your location. The
                  exact appointment time will be confirmed later and may change.
                </p>
                <div className={classes.optionsGrid}>
                  {options.map((option) => {
                    const optionDay = formatOptionDay(option);
                    const optionPeriod = getAppointmentPeriod(option.startts);
                    return (
                      <div key={option.optionid} className={classes.optionCard}>
                        <div className={classes.optionHeader}>
                          <h4 className={classes.optionDay}>{optionDay.day}</h4>
                          <p className={classes.optionDate}>{optionDay.date}</p>
                        </div>
                        <div className={classes.optionBody}>
                          <h3 className={classes.optionTime}>
                            Exact time to be confirmed
                          </h3>
                          {optionPeriod ? (
                            <p className={classes.optionCopy}>
                              Estimated period: {optionPeriod}. Subject to
                              change.
                            </p>
                          ) : null}
                          <p className={classes.optionCopy}>
                            MCSnet install appointment
                          </p>
                          <Button
                            color="primary"
                            disabled={
                              savingOptionId !== null || savingPreference
                            }
                            onClick={() => scheduleOption(option.optionid)}
                          >
                            {savingOptionId === option.optionid
                              ? "Scheduling..."
                              : "Schedule This Install"}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : scheduleData ? (
              <h4 className={classes.cardTitle}>
                No online install scheduling options are available right now.
              </h4>
            ) : null}
            {scheduleData ? (
              <div className={classes.earlierAppointment}>
                <FormControlLabel
                  className={classes.preferenceLabel}
                  control={
                    <Checkbox
                      color="primary"
                      checked={scheduleData.wantsooner === true}
                      disabled={savingPreference || savingOptionId !== null}
                      onChange={(event) =>
                        saveEarlierAppointment(event.target.checked)
                      }
                      inputProps={{
                        "aria-describedby": "earlier-appointment-help",
                      }}
                    />
                  }
                  label="Let me know if an earlier appointment becomes available"
                />
                <p
                  id="earlier-appointment-help"
                  className={classes.preferenceHelp}
                >
                  We’ll contact you if an earlier opening becomes available.
                  Your booked appointment will stay in place unless we arrange a
                  change with you.
                </p>
                <div
                  className={classes.preferenceStatus}
                  role="status"
                  aria-live="polite"
                >
                  {savingPreference
                    ? "Saving preference…"
                    : preferenceSaved
                    ? "Preference saved."
                    : ""}
                </div>
              </div>
            ) : null}
            <div className={classes.support}>
              <PhoneIcon aria-hidden="true" />
              <div>
                <h5>
                  {scheduleData?.scheduled
                    ? "Need help with your appointment?"
                    : "Need a different install date?"}
                </h5>
                <p>
                  {options.length > 0
                    ? "If these dates don’t work for you, call Customer Service and we’ll help you find another option."
                    : "Call Customer Service for help arranging your install appointment."}
                </p>
                <p>
                  <a href="tel:+18663903928;ext=1">
                    <strong>1-866-390-3928 ext. 1</strong>
                  </a>
                  {" · Toll-free"}
                </p>
                <p>Available seven days a week, including holidays.</p>
                <p>
                  <a href="mailto:customerservice@mcsnet.ca">
                    customerservice@mcsnet.ca
                  </a>
                </p>
              </div>
            </div>
            <div className={classes.actions}>
              <Link
                to={config.MYACCOUNT.APP_PATH + "/user/preinstallchecklist"}
                onClick={(event) => {
                  if (savingPreference || savingOptionId !== null) {
                    event.preventDefault();
                  }
                }}
              >
                <Button
                  color="default"
                  disabled={savingPreference || savingOptionId !== null}
                >
                  Back
                </Button>
              </Link>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
};

export default withStyles(styles)(PreinstallSchedule);
